import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contextos/AuthContext';
import { Erro } from '../componentes/Estados';
import { CampoSenha } from '../componentes/Campos';

/**
 * Não existe cadastro público (RN-001). Esta tela só entra ou recupera senha —
 * conta nova é criada por um administrador, a partir de um convite.
 */
const MENSAGENS: Record<string, string> = {
  'auth/invalid-credential': 'E-mail ou senha incorretos.',
  'auth/invalid-email': 'E-mail inválido.',
  'auth/user-disabled': 'Esta conta foi desativada.',
  'auth/too-many-requests': 'Muitas tentativas. Aguarde alguns minutos antes de tentar de novo.',
  'auth/network-request-failed': 'Sem conexão. Verifique a internet e tente de novo.',
};

export function Entrar() {
  const { estado, entrar, recuperarSenha } = useAuth();
  const local = useLocation() as { state?: { de?: string } };
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (estado === 'LIBERADO') return <Navigate to={local.state?.de ?? '/'} replace />;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setAviso(null);
    setEnviando(true);
    try {
      await entrar(email, senha);
    } catch (ex) {
      const codigo = (ex as { code?: string }).code ?? '';
      setErro(MENSAGENS[codigo] ?? 'Não foi possível entrar. Tente de novo.');
    } finally {
      setEnviando(false);
    }
  }

  async function esqueci() {
    if (!email.trim()) {
      setErro('Digite seu e-mail primeiro para receber o link de recuperação.');
      return;
    }
    setErro(null);
    try {
      await recuperarSenha(email);
      setAviso('Se este e-mail tiver conta, o link de recuperação chegou na caixa de entrada.');
    } catch {
      // Mesma mensagem com ou sem conta: não revela quem tem cadastro.
      setAviso('Se este e-mail tiver conta, o link de recuperação chegou na caixa de entrada.');
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-papel p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold tracking-tight">
            Profit<span className="text-marca">Lens</span>
          </h1>
          <p className="mt-1 text-sm text-tinta-2">Análise de viabilidade de compra e revenda</p>
        </div>

        <form
          onSubmit={enviar}
          className="flex flex-col gap-4 rounded-md border border-regua-2 bg-superficie p-6"
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">E-mail</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded border border-regua-2 bg-papel px-3 py-2 text-sm outline-none focus:border-marca"
            />
          </label>

          <CampoSenha rotulo="Senha" valor={senha} aoMudar={setSenha} />

          {erro && <Erro titulo={erro} />}
          {aviso && (
            <p className="rounded border-l-3 border-marca bg-marca/10 px-3 py-2 text-sm text-tinta-2">
              {aviso}
            </p>
          )}

          <button
            type="submit"
            disabled={enviando}
            className="rounded bg-marca px-4 py-2 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
          >
            {enviando ? 'Entrando…' : 'Entrar'}
          </button>

          <button
            type="button"
            onClick={() => void esqueci()}
            className="text-xs text-tinta-3 underline-offset-2 hover:text-marca hover:underline"
          >
            Esqueci minha senha
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-tinta-3">
          O ProfitLens é privado e funciona por convite.{' '}
          <Link to="/primeiro-acesso" className="text-marca underline-offset-2 hover:underline">
            Recebi um convite
          </Link>
        </p>
      </div>
    </main>
  );
}
